import { useState } from "react"
import { useShallow } from "zustand/react/shallow"

// components
import Label from "components/Label/Label"
import { TextInput } from "components-design-system"
import RowTotal from "../RowTotal/RowTotal"
import RowTotalInput from "../RowTotalInput/RowTotalInput"
import RowActions from "../../../components/RowActions/RowActions"
import Cell from "../Cell/Cell"
import CharCounter from "components/CharCounter/CharCounter"
import CellValidation from "../../../components/CellValidation/CellValidation"

// context
import useResultsTableViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useResultsTableViewStore"
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"

const MeasureCategory = ({
  schema,
  measure,
  measureIndex,
  rowIndex,
  category,
  catIndex,
  rows,
  row,
  numOfCategories,
  baselineData,
  setBaselineData,
  canAddCategory,
}) => {
  const { tableView } = useResultsTableViewStore()

  const { readOnly, formView } = useAuthoringViewStore(
    useShallow((state) => ({
      readOnly: state.readOnly,
      formView: state.formView,
    }))
  )

  const [showActions, setShowActions] = useState(false)

  const handleCategoryTitle = (e) => {
    let tempData = { ...baselineData }

    Object.keys(tempData).forEach((registry) => {
      let foundBMIndex = tempData[registry].baseline_measures.findIndex((bm) => bm.id === measure.id)
      let bm = tempData[registry].baseline_measures[foundBMIndex]

      if (foundBMIndex !== -1) {
        let foundRowIndex = bm.rows.findIndex((bmRow) => bmRow.id === row.id)
        if (foundRowIndex !== -1) {
          let catIndex = bm.rows[foundRowIndex].categories.findIndex((item) => item.id === category.id)
          if (catIndex !== -1) {
            for (let rI = 0; rI < bm.rows.length; rI++) {
              bm.rows[rI].categories[catIndex].title = e
            }
          }
        }
      }
    })

    setBaselineData(tempData)
  }

  const canDeleteContCategories = () => {
    return measure.baseline_measure_id.includes("continuous") || measure.baseline_measure_id.includes("customized")
  }
  const canDeleteCategory = () => {
    if (tableView === "ctg" && row.categories.length > 2 && (canAddCategory() || canDeleteContCategories())) return true
    else if (tableView === "eudract" && row.categories.length > 1 && (canAddCategory() || canDeleteContCategories()))
      return true
    return false
  }

  const displayCategoryHeader = () => {
    return (
      <td
        className="col-sm"
        colSpan={1}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        {((numOfCategories > 1 && category.id !== "num-analyzed") || tableView === "eudract") && rowIndex === 0 ? (
          <div id={jpath}>
            <TextInput
              label="Category title"
              readOnly={readOnly || canAddCategory() === false}
              value={category.title}
              onChange={(e) => handleCategoryTitle(e.target.value)}
            />
            <div className="bc-arm-actions">
              {formView.id === "ctg" && !readOnly && (
                <div className="char-counter-container">
                  <CharCounter
                    value={category.title || ""}
                    maxChars={50}
                  />
                </div>
              )}

              {formView.id === "eudra" && !readOnly && !measure.baseline_measure_id.includes("continuous") && (
                <div className="char-counter-container">
                  <CharCounter
                    value={row.title ? `${row.title} ${category.title}` : category.title}
                    maxChars={50}
                  />
                </div>
              )}
              <RowActions
                showActions={showActions}
                setShowActions={setShowActions}
                handleDelete={onDeleteCategory}
                canDelete={canDeleteCategory()}
                deleteModalTitle="Delete row?"
                jpath={`BaselineReportingGroup.bc-${measure.id}-row-${row.id}-category-${category.id}`}
              />
            </div>
          </div>
        ) : (
          <div
            id={jpath}
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Label>{category?.title || ""}</Label>
            <RowActions
              showActions={showActions}
              setShowActions={setShowActions}
              jpath={`BaselineReportingGroup.bc-${measure.id}-row-${row.id}-category-${category.id}`}
            />
          </div>
        )}
        <CellValidation
          table="baseline"
          errorKey={`results_baseline_characteristics_baseline_characteristics_table_${tableView}_baseline_measures_${
            measureIndex + 1
          }_rows_${rowIndex + 1}_categories_${catIndex + 1}`}
        />
      </td>
    )
  }

  const displayValues = () => {
    let values = []

    if (baselineData?.[tableView]?.arms?.length > 0) {
      baselineData?.[tableView]?.arms?.forEach((arm) => {
        category.values.forEach((value) => {
          if (value.arm_id === arm.id) {
            values.push(value)
          }
        })
      })
    }

    if (baselineData?.[tableView]?.subject_analysis_sets?.length > 0) {
      baselineData[tableView].subject_analysis_sets.forEach((sas) => {
        category.values.forEach((value) => {
          if (value.arm_id === sas.id) {
            values.push(value)
          }
        })
      })
    }

    return values.map((value, valIndex) => {
      return (
        <Cell
          key={`cell-value-${measure.id}-${row.id}-${category.id}-${value.arm_id}`}
          schema={schema}
          measure={measure}
          measureIndex={measureIndex}
          row={row}
          category={category}
          rowIndex={rowIndex}
          catIndex={catIndex}
          valIndex={valIndex}
          value={value}
          baselineData={baselineData}
          setBaselineData={setBaselineData}
        />
      )
    })
  }

  const onDeleteCategory = (applyAll) => {
    let tempData = { ...baselineData }
    Object.keys(tempData).forEach((registry) => {
      if (applyAll || tableView === registry) {
        let foundBMIndex = tempData[registry].baseline_measures.findIndex((bm) => bm.id === measure.id)
        if (foundBMIndex !== -1) {
          let bm = tempData[registry].baseline_measures[foundBMIndex]

          let foundRowIndex = bm.rows.findIndex((bmRow) => bmRow.id === row.id)
          if (foundRowIndex !== -1) {
            let catIndex = bm.rows[foundRowIndex].categories.findIndex((item) => item.id === category.id)
            if (catIndex !== -1) {
              bm.rows.forEach((row) => {
                row.categories.splice(catIndex, 1)
              })
            }
          }
        }
      }
    })
    setBaselineData(tempData)
  }

  let jpath = `baseline_characteristics:${row.id}-${category.id}`

  const displayCtgRowTotal = () => {
    return category.id === "num-analyzed" ? (
      <RowTotal
        category={category}
        measure={measure}
        baselineData={baselineData}
      />
    ) : (
      <RowTotalInput
        category={category}
        rowIndex={rowIndex}
        catIndex={catIndex}
        baselineData={baselineData}
        setBaselineData={setBaselineData}
        measure={measure}
        rows={rows}
      />
    )
  }

  const displayEudractRowTotal = () => {
    return (
      <RowTotal
        baselineData={baselineData}
        hideTotal={measure?.baseline_measure_id?.includes("continuous")}
        category={category}
      />
    )
  }

  const getRegionEnrollment = () => {
    if (tableView === "eudract") {
      let foundCtgBaseline = baselineData?.ctg?.baseline_measures.find((bm) => bm.id === measure.id)
      if (foundCtgBaseline?.baseline_measure_id === "ctg-region-categorical") {
        return true
      }
    }
    return false
  }

  return (
    <>
      {displayCategoryHeader()}
      {displayValues()}
      {tableView === "ctg" && displayCtgRowTotal()}
      {tableView === "eudract" && displayEudractRowTotal()}
    </>
  )
}

export default MeasureCategory
